import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { SettingsNav } from "@/components/SettingsNav";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Settings, Loader2, Save } from "lucide-react";

const FIELDS = [
  ["name", "Nome azienda"], ["legal_name", "Ragione sociale"], ["vat_number", "Partita IVA"],
  ["tax_code", "Codice fiscale"], ["industry", "Settore"], ["phone", "Telefono"],
  ["email", "Email"], ["pec", "PEC"], ["website", "Sito"], ["address", "Indirizzo"],
  ["city", "Città"], ["province", "Provincia"], ["zip_code", "CAP"], ["country", "Nazione"],
  ["language", "Lingua"], ["currency", "Valuta"], ["timezone", "Fuso orario"],
];

export default function CompanySettings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = (user?.permissions || []).includes("settings.manage");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ["tenant"], queryFn: async () => (await api.get("/tenant")).data.data });
  useEffect(() => { if (data) setForm(data); }, [data]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(FIELDS.map(([k]) => [k, form[k]]).concat([["logo_url", form.logo_url], ["primary_color", form.primary_color], ["secondary_color", form.secondary_color], ["favicon_url", form.favicon_url], ["display_name", form.display_name]]));
      await api.patch("/tenant", payload);
      toast.success("Impostazioni azienda salvate");
      qc.invalidateQueries({ queryKey: ["tenant"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (isLoading || !form) return <div><PageHeader title="Impostazioni" icon={Settings} /><Skeleton className="h-96 rounded-xl" /></div>;

  return (
    <div>
      <PageHeader title="Impostazioni" subtitle="Gestisci le informazioni e il branding della tua azienda" icon={Settings}
        actions={canManage && <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-company-btn">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva</Button>} />
      <SettingsNav />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-testid="company-form">
          <CardHeader><CardTitle className="text-base">Dati aziendali</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-2">
                <Label>{label}</Label>
                <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} disabled={!canManage} data-testid={`company-${k}`} />
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Branding</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label>URL Logo</Label><Input value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} disabled={!canManage} placeholder="https://..." /></div>
              <div className="space-y-2"><Label>Nome visualizzato</Label><Input value={form.display_name || ""} onChange={(e) => set("display_name", e.target.value)} disabled={!canManage} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Colore principale</Label><Input type="color" value={form.primary_color || "#10b981"} onChange={(e) => set("primary_color", e.target.value)} disabled={!canManage} className="h-10 p-1" /></div>
                <div className="space-y-2"><Label>Secondario</Label><Input type="color" value={form.secondary_color || "#0f172a"} onChange={(e) => set("secondary_color", e.target.value)} disabled={!canManage} className="h-10 p-1" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Anteprima</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 rounded-xl border border-border p-4">
                {form.logo_url ? <img src={form.logo_url} alt="logo" className="h-10 w-10 rounded-lg object-contain" /> :
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg text-white" style={{ background: form.primary_color }}>{(form.display_name || form.name || "C")[0]}</div>}
                <span className="font-heading font-semibold" style={{ color: form.secondary_color }}>{form.display_name || form.name}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
